import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Snackbar, Stack, TextField, Typography, Autocomplete } from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import AddCardResponsible from '@/features/responsible/AddCardResponsible.tsx';
import AddResponsible from '@/features/responsible/AddResponsible.tsx';
import CreateColumnCrm from '@/features/crm/CreateColumnCrm';
import { TypesCRM } from '@/types/crm.ts';
import useModalStore from '@/features/responsible/hooks/useModal.tsx';
import { TitlePages } from '@/components/Base/TitlePage';
import { useNavigate } from 'react-router-dom';
import { CardFilters } from '@/features/crm/types';
import { CrmColumnsContainer } from '@/features/crm/CrmColumnsContainer';
import useLoadingActions from '@/hooks/useLoadingActions';
import { useSeries } from '@/features/crm/api/listFilterOptions';
import useDebounce from '@/hooks/useDebounce';

type CrmPageNewProps = {
  title: string;
  type: TypesCRM;
};

export const CrmPageNew: React.FC<CrmPageNewProps> = ({ title, type }) => {
  const { setIsLoading } = useLoadingActions();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<CardFilters>({
    nome: '',
    serie: undefined,
    turma: undefined,
    turno: undefined
  });

  const [localNome, setLocalNome] = useState('');
  const [localSerie, setLocalSerie] = useState<string | number>('all');

  const [searchSerie, setSearchSerie] = useState('');

  const debouncedSearchSerie = useDebounce(searchSerie, 300);

  const { data: seriesData = [], isLoading: loadingSeries } = useSeries({
    serie_nome: debouncedSearchSerie || undefined
  });

  const seriesOptions = [
    ...(Array.isArray(seriesData) ? seriesData.map(s => ({
      id: s.id,
      label: `${s.nome} - ${s.turma} - ${s.turno}`,
      searchText: `${s.nome} ${s.curso} ${s.turma} ${s.turno} ${s.periodo}`.toLowerCase()
    })) : [])
  ];

  const [totalVagas, setTotalVagas] = useState<number>(0);

  const {
    openResponsible,
    openCardResponsible,
    toggleResponsible,
    toggleCardResponsible,
    setType,
  } = useModalStore((state) => state);

  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (type) {
      setType(type);
      setIsLoading(false, '');
    }
  }, [type, setType, setIsLoading]);

  const handleClose = () => {
    toggleResponsible(false);
  };

  const handleCloseCard = () => {
    toggleCardResponsible(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const onApplyFilter = () => {
    // Aplica os filtros convertendo os valores
    const newFilters: CardFilters = {
      nome: localNome || undefined,
      serie: localSerie !== 'all' ? localSerie : undefined,
    };

    setFilters(newFilters);
  };

  return (
    <>
      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack flexDirection={'row'} alignItems={'center'} gap={4}>
          <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          <TitlePages title={title} />
        </Stack>

        {type === TypesCRM.MATRICULA && (
          <Button
            sx={{
              p: '0px, 16px, 0px, 16px',
              borderRadius: '24px',
              fontSize: '14px',
              height: '37px',
              color: '#fff !important',
              weight: '600',
              background: '#15AB51',
              fontWeight: '700',
              textTransform: 'capitalize',
            }}
            variant="contained"
            onClick={() => toggleResponsible(true)}
          >
            <Add />
            Cadastrar responsável financeiro
          </Button>
        )}
      </Stack>

      <Box sx={{ backgroundColor: 'transparent' }}>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'end',
            p: '15px',
            '.MuiButtonBase-root': { color: '#000' },
            '.MuiTypography-root': { color: '#000' },
            background: '#FFF',
            borderRadius: '8px'
          }}
        >
          <Box maxWidth={'350px'} width={'100%'}>
            <Typography mb={'7px'} fontWeight={600}>Nome</Typography>
            <TextField
              name={'nome'}
              value={localNome}
              onChange={(e) => setLocalNome(e.target.value)}
              placeholder="Digite o nome..."
              fullWidth
              sx={{
                border: "1px solid black",
                borderRadius: '6px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' }
                },
                '& .MuiInputBase-input': {
                  color: '#000'
                }
              }}
            />
          </Box>

          <Box maxWidth={'600px'} width={'100%'}>
            <Typography mb={'7px'} fontWeight={600}>Série</Typography>
            <Autocomplete
              options={seriesOptions}
              getOptionLabel={(option) => option.label || ''}
              value={seriesOptions.find(s => s.id === localSerie) || null}
              onChange={(e, newValue) => {
                setLocalSerie(newValue?.id || 'all');
              }}
              onInputChange={(e, newInputValue, reason) => {
                // Só atualiza a busca se o usuário estiver digitando
                if (reason === 'input') {
                  setSearchSerie(newInputValue);
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingSeries}
              loadingText="Buscando séries..."
              noOptionsText={searchSerie ? "Nenhuma série encontrada" : "Digite para buscar séries"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Digite nome, turma ou turno..."
                  sx={{
                    border: '1px solid black',
                    borderRadius: '6px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              )}
            />
          </Box>

          <Button
            sx={{
              maxWidth: '200px',
              p: '0 5px',
              borderRadius: '24px',
              fontSize: '14px',
              height: '37px',
              color: '#fff !important',
              width: '188px',
              weight: '600',
              background: '#15AB51',
              textTransform: 'capitalize'
            }}
            variant="contained"
            onClick={onApplyFilter}
          >
            Filtrar
          </Button>

          <Box
            sx={{
              border: '2px solid red',
              borderRadius: '6px',
              backgroundColor: 'rgba(252, 228, 212, 1)',
              p: '16px',
              minWidth: '200px',
              color: ' rgba(52, 52, 52, 1)',
              fontWeight: 600,
            }}
          >
            Vagas: {totalVagas}
          </Box>
        </Box>

        <Stack flexDirection={'row'}>
          <CrmColumnsContainer
            type={type}
            filters={filters}
            setOpenModal={setOpenModal}
            onTotalVagasChange={setTotalVagas}
          />
        </Stack>

        {openResponsible && <AddResponsible handleClose={handleClose} />}

        {openCardResponsible && <AddCardResponsible handleClose={handleCloseCard} type={type} />}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity as 'success' | 'error'}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {openModal && <CreateColumnCrm handleClose={() => setOpenModal(false)} type={type} />}
    </>
  );
};
